import React, { useEffect, useState } from "react";
import {
    FaLock,
    FaSeedling,
    FaBullseye,
    FaDownload,
    FaEthereum,
    FaBitcoin,
    FaBalanceScale,
    FaShieldAlt,
    FaCogs,
    FaHandshake,
} from "react-icons/fa";
import {
    MdOutlineVerifiedUser,
    MdOutlineGeneratingTokens,
    MdPriceCheck,
    MdToken,
} from "react-icons/md";
import { CiBookmarkCheck } from "react-icons/ci";
import { BsCalendar2DateFill } from "react-icons/bs";
import { PieChart, Pie, Cell, Sector } from "recharts";
import { motion } from "framer-motion";
import { Download } from "lucide-react";
import { db } from "../firebase/firebase";
import { getDocs, query, collection, orderBy } from "firebase/firestore";
const data = [
    { name: "Legal Structure & Licensing", value: 25, color: "#6C63FF", icon: <FaBalanceScale /> },
    { name: "Platform Development", value: 20, color: "#FF9F40", icon: <FaShieldAlt /> },
    { name: "Marketing’s Partnerships", value: 20, color: "#9F5FFF", icon: <FaCogs /> },
    { name: "Community Partnerships", value: 15, color: "#00C49F", icon: <FaHandshake /> },
];

const RADIAN = Math.PI / 180;
type LabelProps = {
    cx: number;
    cy: number;
    midAngle: number;
    innerRadius: number;
    outerRadius: number;
    index: number;
};

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, index }: LabelProps) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const textAnchor: "start" | "end" = x > cx ? "start" : "end";
    return (
        <g>
            {/* Icon inside slice */}
            <foreignObject
                x={cx + (innerRadius + (outerRadius - innerRadius) * 0.5) * Math.cos(-midAngle * RADIAN) - 12}
                y={cy + (innerRadius + (outerRadius - innerRadius) * 0.5) * Math.sin(-midAngle * RADIAN) - 12}
                width={24}
                height={24}
                style={{ overflow: "visible" }}
            >
                <div className="text-white text-lg flex justify-center items-center w-6 h-6">
                    {data[index].icon}
                </div>
            </foreignObject>

            {/* Label outside slice */}
            <text
                x={cx + (innerRadius + (outerRadius - innerRadius) * 1.5) * Math.cos(-midAngle * RADIAN)}
                y={cy + (innerRadius + (outerRadius - innerRadius) * 1.5) * Math.sin(-midAngle * RADIAN)}
                textAnchor={textAnchor}
                dominantBaseline="start"
                fill="#95e999"
                fontSize={12}
                className="hidden sm:block"
            >
                {data[index].name}
            </text>
        </g>
    );
};

const PresaleOverview = () => {
    const [saftUrl, setSaftUrl] = useState("");
    useEffect(() => {
        const fetchURL = async () => {
            const q = query(collection(db, "saft_contracts"), orderBy("createdAt", "desc"));
            const snapshot = await getDocs(q);
            const latest = snapshot.docs[0]?.data();
            if (latest?.url) {
                setSaftUrl(latest.url);
            }
        };
        fetchURL();
    }, []);
    return (
        <div id="stake" className="w-full bg-gradient-to-b from-[#382057] to-[#1C1E2A]">
            <div className="w-full max-w-7xl text-white px-4 py-12 rounded-xl mx-auto shadow-2xl">
                <div className="text-center mb-12">
                    <motion.h1
                        initial={{ opacity: 0, y: -30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-3xl md:text-5xl font-bold mb-4"
                    >
                        Secure Your Stake in the <br />Future of Mineral Intelligence
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="text-gray-300 max-w-2xl text-lg mx-auto"
                    >
                        Join Mineralex’s exclusive SAFT-based presale and become part of the next generation of decentralized mineral exploration.
                    </motion.p>
                    <a href="/contact_Us">
                        <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.95 }}
                            className="mt-6 px-6 py-3 bg-gradient-to-r from-purple-500 to-orange-400 text-white rounded-full font-semibold shadow-lg"
                        >
                            Participate in the Presale
                        </motion.button>
                    </a>
                </div>

                <div className="grid grid-cols-1 gap-8">
                    <div className="space-y-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <motion.div
                                whileHover={{ scale: 1.03 }}
                                className="w-full  bg-[#111828] p-6 rounded-lg">
                                <h2 className="text-lg font-semibold mb-4">Mineralex Rounds Plan</h2>
                                <div className="flex flex-row justify-around items-center gap-4 text-sm mt-8">
                                    <div className="flex flex-col justify-center items-center gap-2">
                                        <div className="p-4 rounded-full bg-[#313564] shadow-lg">
                                            <FaLock className="text-4xl text-[#959ce9]" />
                                        </div>
                                        <div>
                                            Private Round
                                        </div>
                                    </div>

                                    <div className="flex flex-col justify-center items-center gap-2">
                                        <div className="p-4 rounded-full bg-[#313564] shadow-lg">
                                            <FaSeedling className="text-4xl text-green-400" />
                                        </div>
                                        <div>
                                            Seed Round
                                        </div>
                                    </div>

                                    <div className="flex flex-col justify-center items-center gap-2">
                                        <div className="p-4 rounded-full bg-gradient-to-b from-[#79504a] to-[#1C1E2A] shadow-lg">
                                            <FaBullseye className="text-4xl text-orange-400" />
                                        </div>
                                        <div>
                                            Public Sale
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                            <motion.div
                                whileHover={{ y: -5 }}
                                className="bg-[#111828] p-6 rounded-lg flex-1 text-center"
                            >
                                <img src="/presale_Token/shield.svg" alt="shield" className="w-20 h-20 mx-auto mb-4" />
                                <h2 className="text-lg font-semibold mb-4">SAFT Framework</h2>
                                {saftUrl ? (
                                    <a
                                        href={saftUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full max-w-56 mx-auto flex justify-center items-center gap-2 text-sm  py-2 bg-blue-600 hover:bg-blue-500 transition rounded-full"
                                    >
                                        <FaDownload />   Download SAFT Agreement
                                    </a>
                                ) : (
                                    <p>SAFT Agreement file doesn't exist!</p>
                                )}
                            
                            </motion.div>
                        </div>
                        <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="w-full flex flex-col justify-between items-center">
                                <motion.div
                                    whileHover={{ scale: 1.03 }}
                                    className="w-full h-full bg-[#111828] p-6 rounded-lg">
                                    <h2 className="text-lg font-semibold mb-4">Presale Details</h2>
                                    <div className="text-sm space-y-2">
                                        <div className="flex items-center gap-2">
                                            <div className="p-1 rounded-full bg-[#313564] shadow-lg">
                                                <MdOutlineGeneratingTokens className="text-lg text-[#959ce9]" />
                                            </div>
                                            <div><span className="font-medium text-blue-400">Token:</span> MXUT</div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div className="p-1 rounded-full bg-[#313564] shadow-lg">
                                                <MdPriceCheck className="text-lg text-[#959ce9]" />
                                            </div>
                                            <div><span className="font-medium text-blue-400">Price:</span> $0.005</div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div className="p-1 rounded-full bg-[#313564] shadow-lg">
                                                <MdToken className="text-lg text-[#959ce9]" />
                                            </div>
                                            <div><span className="font-medium text-blue-400">Supply:</span> 80,000,000 future MXUT</div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div className="p-1 rounded-full bg-[#313564] shadow-lg">
                                                <CiBookmarkCheck className="text-lg text-[#959ce9]" />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-blue-400">Accepted:</span>
                                                <FaEthereum className="text-teal-300" />ETH
                                                <FaBitcoin className="text-yellow-400" />BTC
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div className="p-1 rounded-full bg-[#313564] shadow-lg">
                                                <BsCalendar2DateFill className="text-lg text-[#959ce9]" />
                                            </div>
                                            <div><span className="font-medium text-blue-400">Date:</span> June 20 – July 30, 2025</div>
                                        </div>
                                    </div>
                                </motion.div>
                                {/* Compliance Guarantee */}
                                <motion.div
                                    whileHover={{ scale: 1.03 }}
                                    className="w-full bg-[#111828] mt-6 p-6 rounded-lg flex items-center gap-3 text-sm">
                                    <MdOutlineVerifiedUser className="text-blue-400 text-6xl" />
                                    <span>Investor Security & Regulatory Compliance Guaranteed</span>
                                </motion.div>
                            </div>
                            <motion.div
                                whileHover={{ scale: 1.03 }}
                                className="bg-[#111828] text-white rounded-xl"
                            >
                                <h2 className="text-lg font-semibold p-4 text-center">Use of Funds</h2>
                                <div className="flex flex-col items-center gap-4 p-4">
                                    <PieChart width={480} height={250}>
                                        <Pie
                                            data={data}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={20}
                                            outerRadius={80}
                                            paddingAngle={2}
                                            dataKey="value"
                                            label={renderCustomizedLabel}
                                        >
                                            {data.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                    <div className="w-full text-center text-xl">Under Construction </div>
                                    <div className="space-y-2 text-sm w-full">
                                        {data.map((item, index) => (
                                            <div key={index} className="flex items-center gap-2">
                                                <span style={{ color: item.color }}>{item.icon}</span>
                                                <span style={{ color: item.color }}>{item.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>

                </div>

                <div className="text-center mt-12">
                    <motion.button
                        whileHover={{ scale: 1.03 }}
                        className="px-6 py-3 bg-gradient-to-r from-purple-500 to-orange-400 text-white rounded-full font-semibold shadow-lg"
                    >
                        Participate in Presale Now →
                    </motion.button>
                </div>
            </div>
        </div>

    );
};

export default PresaleOverview;
